import React, { useEffect, useState } from "react";
import Space from "../ui/Space";
import Label from "../ui/Label";
import Input from "../ui/Input";
import PriceField from "../ui/PriceField";
import Status from "../ui/Status";
import { InfoIcon } from "../utils/Icons";
import { useTabs } from "../hooks/useTabs";
import { listen } from "../hooks/Listener";
import { KBD } from "../utils/Keybindings";
import { createModal } from "../hooks/createModal";
import { ROUTES } from "../utils/Routes";
import { pubCall } from "../utils/PubSub";
import { showError } from "../ui/Message";

function BatchPopup(props) {
    const {
        route = "",
        controller = "",
        callback = () => { },
    } = props;

    const makeTabs = () => [
        { label: "count", key: "count", value: 1 },
        { label: "amount", key: "amount", value: 0 },
        { label: "note", key: "note", value: "" },
    ];

    const {
        tabs,
        index,
        update,
        getVal,
        editProps,
        next,
        prev,
        refs,
    } = useTabs(makeTabs(), "count");

    const save = (overrides = {}) => {
        const count = Number(overrides.count ?? getVal("count")) || 0;
        const amount = Number(overrides.amount ?? getVal("amount")) || 0;
        const note = overrides.note ?? getVal("note") ?? "";

        if (!count || count < 1) {
            refs.current?.count?.focus?.();
            refs.current?.count?.select?.();
            return;
        }

        if (!amount) {
            refs.current?.amount?.focus?.();
            refs.current?.amount?.select?.();
            return;
        }

        callback({
            count,
            amount,
            note,
        });
    };

    React.useEffect(() => {
        requestAnimationFrame(() => {
            refs.current?.[index]?.focus?.();
            refs.current?.[index]?.select?.();
        });
    }, [index]);

    React.useEffect(() => {
        const cleanup = listen(
            controller,
            route,
            [
                {
                    key: KBD.ESCAPE,
                    action: () => callback(null),
                },
                {
                    key: KBD.ARROW_DOWN,
                    action: () => next(),
                },
                {
                    key: KBD.ARROW_UP,
                    action: () => prev(),
                },
                {
                    key: KBD.PAGE_DOWN,
                    action: () => save(),
                },
            ],
            false,
            [KBD.ESCAPE, KBD.ARROW_DOWN, KBD.ARROW_UP, KBD.PAGE_DOWN]
        );

        return cleanup;
    }, [index, tabs]);

    return (
        <div style={{ width: 430, padding: 15 }}>
            <Space>
                <Label style={{ width: 150 }}>Number of cards:</Label>
                <Input
                    {...editProps("count")}
                    edit
                    number
                    right
                    width={80}
                    maxLength={4}
                    onEnter={(count) => {
                        update("count", count);
                        next();
                    }}
                    onPageDown={(count) => {
                        save({ count });
                    }}
                />
            </Space>

            <div className="shim" />

            <Space>
                <Label style={{ width: 150 }}>Value per card:</Label>
                <PriceField
                    {...editProps("amount")}
                    edit
                    right
                    width={110}
                    onEnter={(amount) => {
                        update("amount", amount);
                        next();
                    }}
                    onPageDown={(amount) => {
                        save({ amount });
                    }}
                />
            </Space>

            <div className="shim" />

            <Space>
                <Label style={{ width: 150 }}>Note on each card:</Label>
                <Input
                    {...editProps("note")}
                    edit
                    width={220}
                    maxLength={30}
                    onEnter={(note) => {
                        update("note", note);
                        save({ note });
                    }}
                    onPageDown={(note) => {
                        save({ note });
                    }}
                />
            </Space>

            <div className="shim" />

            <Status>
                <InfoIcon />
                Enter number of cards, value per card, and note. Page Down saves.
            </Status>
        </div>
    );
}

export default function POSNewGiftCard(props) {
    const {
        controller = "",
        route = "",
        callback = () => { },
    } = props;

    const [batch, setBatch] = useState(null);
    const [gc, setGC] = useState();

    const makeTabs = () => [
        { label: "amount", key: "amount", value: 0 },
        { label: "note", key: "note", value: "" },
        { label: "batch", key: "batch", value: "N" },
    ];

    const { tabs, index, setIndex, update, getVal, editProps, next, prev, refs, } = useTabs(makeTabs(), "amount");

    const genGiftCard = () => {
        pubCall("gift_card.create", {}, (_s, _r) => {
            if (_s) {
                setGC(_r);
            } else {
                showError(_r);
            }
        })
    }
    useEffect(genGiftCard, []);


    const openBatchPopup = () => {
        const returnRoute = window.bm_state.value;
        window.bm_state.value = ROUTES.POS.NEW_GIFT_CARD_BATCH;

        createModal({
            component: (
                <BatchPopup
                    controller={controller}
                    route={ROUTES.POS.NEW_GIFT_CARD_BATCH}
                    callback={(e) => {
                        window.bm_state.value = returnRoute;

                        if (e) {
                            setBatch(e);
                            update("batch", "Y");
                        } else {
                            update("batch", "N");
                        }

                        requestAnimationFrame(() => {
                            refs.current?.batch?.focus?.();
                        });
                    }}
                />
            ),
            closable: true,
            title: "Create Batch",
            callback: () => {
                window.bm_state.value = returnRoute;

                requestAnimationFrame(() => {
                    refs.current?.batch?.focus?.();
                });
            },
        });
    };

    const save = (overrides = {}) => {
        const amount = Number(overrides.amount ?? getVal("amount")) || 0;
        const note = overrides.note ?? getVal("note") ?? "";
        const batchValue = overrides.batch ?? getVal("batch") ?? "N";

        if (!amount) {
            return;
        }

        callback({
            amount,
            note,
            batch: batchValue === "Y" ? batch : null,
        });
    };

    const setBatchValue = (value) => {
        const nextValue = String(value || "").toUpperCase();

        if (nextValue === "Y") {
            update("batch", "Y");
            openBatchPopup();
            return;
        }

        update("batch", "N");
        setBatch(null);
    };

    React.useEffect(() => {
        requestAnimationFrame(() => {
            refs.current?.[index]?.focus?.();
            refs.current?.[index]?.select?.();
        });
    }, [index]);

    React.useEffect(() => {
        const cleanup = listen(
            controller,
            route,
            [
                {
                    key: KBD.ESCAPE,
                    action: () => callback(null),
                },
                {
                    key: KBD.ARROW_DOWN,
                    action: () => next(),
                },
                {
                    key: KBD.ARROW_UP,
                    action: () => prev(),
                },
                {
                    key: KBD.PAGE_DOWN,
                    action: () => save(),
                },
            ],
            false,
            [KBD.ESCAPE, KBD.ARROW_DOWN, KBD.ARROW_UP, KBD.PAGE_DOWN]
        );

        return cleanup;
    }, [index, tabs, batch]);

    return (
        <div style={{ width: 420, padding: 15 }}>

            {/* <pre>{JSON.stringify(gc, null, 2)}</pre> */}
            <Space>
                <Label style={{ width: 140 }}>Gift Card# </Label>
                <Label style={{ "color" : "var(--text)" }}>Gift Card# </Label>
            </Space>

            <Space>
                <Label style={{ width: 140 }}>Amount to issue:</Label>
                <PriceField
                    {...editProps("amount")}
                    ref={(el) => refs.current.amount = el}
                    edit
                    width={110}
                    right
                    onEnter={(amount) => {
                        update("amount", amount);
                        next();
                    }}
                    onPageDown={(amount) => {
                        save({ amount });
                    }}
                />
            </Space>

            <div className="shim" />

            <Space>
                <Label style={{ width: 140 }}>Note:</Label>
                <Input
                    {...editProps("note")}
                    edit
                    width={220}
                    maxLength={30}
                    onEnter={(note) => {
                        update("note", note);
                        next();
                    }}
                    onPageDown={(note) => {
                        save({ note });
                    }}
                />
            </Space>

            <div className="shim" />

            <Space>
                <Label style={{ width: 140 }}>Create batch?</Label>
                <Input
                    {...editProps("batch")}
                    edit
                    uppercase
                    width={40}
                    maxLength={1}
                    chars={["Y", "N"]}
                    onChange={setBatchValue}
                    onEnter={(value) => {
                        setBatchValue(value);

                        if (String(value || "").toUpperCase() !== "Y") {
                            save({ batch: "N" });
                        }
                    }}
                    onPageDown={(value) => {
                        const nextValue = String(value || "").toUpperCase();

                        if (nextValue === "Y") {
                            setBatchValue("Y");
                        } else {
                            save({ batch: "N" });
                        }
                    }}
                />

                {batch && (
                    <Label secondary>
                        {batch.count} card{batch.count === 1 ? "" : "s"}
                    </Label>
                )}
            </Space>

            <div className="shim" />

            <Status>
                <InfoIcon />
                
            </Status>
        </div>
    );
}